// SPDX-License-Identifier: Apache-2.0

// search.ts — chained cosineSim → topK pipeline.
// One command encoder, two compute passes, single readback of just the partials buffer.
// CPU-side merge of n_workgroups × K partials produces the final top-K.

import topKShader from "./topK.wgsl?raw";
import {
  type CosineSimSetup,
  setupCosineSim,
} from "./cosineSim";

export interface SearchSetup {
  cosineSim: CosineSimSetup;
  topKPipeline: GPUComputePipeline;
  topKBindGroupLayout: GPUBindGroupLayout;
  topKParamsBuf: GPUBuffer;
  partialsBuf: GPUBuffer;
  partialsReadbackBuf: GPUBuffer;
  k: number;
  nWorkgroups: number;
}

export interface SearchResult {
  topK: { idx: number; sim: number }[];
  /** Wall time from encoder start through partials readback unmap. */
  totalMs: number;
  /** Time spent in the CPU-side merge of partials. */
  mergeMs: number;
  /** Number of candidates the CPU merged (n_workgroups × k). */
  candidatesScanned: number;
  /** Range of sim values seen across all partials — for diagnostic, catches silent kernel failures. */
  simRange: { min: number; max: number };
}

const TILE = 1024;

export async function setupSearch(
  device: GPUDevice,
  nDocs: number,
  dim: number,
  k: number,
): Promise<SearchSetup> {
  if (k > 1024) throw new Error("k must be <= 1024 (workgroup tile)");
  if (k < 1) throw new Error("k must be >= 1");

  const cosineSim = await setupCosineSim(device, nDocs, dim);
  const nWorkgroups = Math.ceil(nDocs / TILE);

  const module = device.createShaderModule({ code: topKShader, label: "topK" });
  // Surface WGSL compilation errors that would otherwise be silent.
  const info = await module.getCompilationInfo();
  const errors = info.messages.filter((m) => m.type === "error");
  if (errors.length > 0) {
    const text = errors.map((e) => `  L${e.lineNum}:${e.linePos} ${e.message}`).join("\n");
    throw new Error(`topK WGSL compile errors:\n${text}`);
  }
  const warnings = info.messages.filter((m) => m.type === "warning");
  if (warnings.length > 0) {
    console.warn("[topK shader] compile warnings:", warnings);
  }

  const topKBindGroupLayout = device.createBindGroupLayout({
    label: "topK-bgl",
    entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
    ],
  });
  const pipelineLayout = device.createPipelineLayout({
    bindGroupLayouts: [topKBindGroupLayout],
  });
  const topKPipeline = device.createComputePipeline({
    label: "topK",
    layout: pipelineLayout,
    compute: { module, entryPoint: "main" },
  });

  const topKParamsBuf = device.createBuffer({
    label: "topK-params",
    size: 16,
    usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
  });
  const partialsBuf = device.createBuffer({
    label: "topK-partials",
    size: nWorkgroups * k * 8, // vec2<f32>
    usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC,
  });
  const partialsReadbackBuf = device.createBuffer({
    label: "topK-partials-readback",
    size: nWorkgroups * k * 8,
    usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
  });

  // Static params written once (n and k don't change between queries).
  const paramsArr = new Uint32Array([nDocs, k, 0, 0]);
  device.queue.writeBuffer(topKParamsBuf, 0, paramsArr.buffer);

  return {
    cosineSim,
    topKPipeline,
    topKBindGroupLayout,
    topKParamsBuf,
    partialsBuf,
    partialsReadbackBuf,
    k,
    nWorkgroups,
  };
}

export async function runSearch(
  setup: SearchSetup,
  query: Int8Array,
  queryScale: number,
): Promise<SearchResult> {
  const {
    cosineSim,
    topKPipeline,
    topKBindGroupLayout,
    topKParamsBuf,
    partialsBuf,
    partialsReadbackBuf,
    k,
    nWorkgroups,
  } = setup;
  const { device } = cosineSim;

  // Per-query uploads: query bytes, query scale, cosineSim params.
  device.queue.writeBuffer(cosineSim.queryBuf, 0, query.buffer, query.byteOffset, query.byteLength);
  device.queue.writeBuffer(
    cosineSim.scalesBuf,
    cosineSim.nDocs * 4,
    new Float32Array([queryScale]).buffer,
  );
  const cosParams = new Uint32Array([cosineSim.nDocs, cosineSim.dimPacked, 0, 0]);
  device.queue.writeBuffer(cosineSim.paramsBuf, 0, cosParams.buffer);

  const cosBindGroup = device.createBindGroup({
    layout: cosineSim.bindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: cosineSim.paramsBuf } },
      { binding: 1, resource: { buffer: cosineSim.corpusBuf } },
      { binding: 2, resource: { buffer: cosineSim.queryBuf } },
      { binding: 3, resource: { buffer: cosineSim.scalesBuf } },
      { binding: 4, resource: { buffer: cosineSim.simsBuf } },
    ],
  });
  const topKBindGroup = device.createBindGroup({
    layout: topKBindGroupLayout,
    entries: [
      { binding: 0, resource: { buffer: topKParamsBuf } },
      { binding: 1, resource: { buffer: cosineSim.simsBuf } }, // chains directly off cosineSim
      { binding: 2, resource: { buffer: partialsBuf } },
    ],
  });

  const t0 = performance.now();
  // Catch validation errors that would otherwise just log to the console.
  device.pushErrorScope("validation");
  const encoder = device.createCommandEncoder({ label: "search-encoder" });

  const pass1 = encoder.beginComputePass({ label: "search-cosineSim" });
  pass1.setPipeline(cosineSim.pipeline);
  pass1.setBindGroup(0, cosBindGroup);
  pass1.dispatchWorkgroups(Math.ceil(cosineSim.nDocs / 64));
  pass1.end();

  // WebGPU spec §21.5: implicit barrier between passes that share a buffer.
  const pass2 = encoder.beginComputePass({ label: "search-topK" });
  pass2.setPipeline(topKPipeline);
  pass2.setBindGroup(0, topKBindGroup);
  pass2.dispatchWorkgroups(nWorkgroups);
  pass2.end();

  encoder.copyBufferToBuffer(partialsBuf, 0, partialsReadbackBuf, 0, nWorkgroups * k * 8);
  device.queue.submit([encoder.finish()]);

  const validationError = await device.popErrorScope();
  if (validationError) {
    throw new Error(`WebGPU validation error during search: ${validationError.message}`);
  }

  await partialsReadbackBuf.mapAsync(GPUMapMode.READ);
  const raw = partialsReadbackBuf.getMappedRange();
  const buf = raw.slice(0); // own-buffer copy so we can unmap immediately
  partialsReadbackBuf.unmap();
  const partialsF32 = new Float32Array(buf);
  const partialsU32 = new Uint32Array(buf);

  // CPU merge: scan n_workgroups × K partials, sort by sim desc, take top K.
  const tMerge0 = performance.now();
  const candidates: { sim: number; idx: number }[] = [];
  const total = nWorkgroups * k;
  let simMin = Infinity;
  let simMax = -Infinity;
  for (let e = 0; e < total; e++) {
    const sim = partialsF32[e * 2] ?? 0;
    const idx = partialsU32[e * 2 + 1] ?? 0;
    if (idx === 0xffffffff) continue; // padding
    if (sim < simMin) simMin = sim;
    if (sim > simMax) simMax = sim;
    candidates.push({ sim, idx });
  }
  candidates.sort((a, b) => b.sim - a.sim);
  const topK = candidates.slice(0, k);
  const tMerge1 = performance.now();

  const t1 = performance.now();

  // Diagnostic: if every candidate has sim==0 AND idx==0, the kernel didn't write.
  // This used to happen silently when the corpus exceeded maxStorageBufferBindingSize.
  if (candidates.length > 0 && simMin === 0 && simMax === 0 && candidates.every((c) => c.idx === 0)) {
    throw new Error(
      "topK partials look uninitialized (all zeros). cosineSim or topK kernel likely didn't run — " +
      "check device.limits.maxStorageBufferBindingSize vs corpus size, and the browser console for WebGPU validation errors.",
    );
  }

  return {
    topK,
    totalMs: t1 - t0,
    mergeMs: tMerge1 - tMerge0,
    candidatesScanned: total,
    simRange: { min: simMin === Infinity ? 0 : simMin, max: simMax === -Infinity ? 0 : simMax },
  };
}

export function disposeSearch(setup: SearchSetup): void {
  setup.topKParamsBuf.destroy();
  setup.partialsBuf.destroy();
  setup.partialsReadbackBuf.destroy();
  // The cosineSim setup is owned by the caller (or composed disposal below).
}

export function disposeSearchAll(setup: SearchSetup): void {
  disposeSearch(setup);
  setup.cosineSim.paramsBuf.destroy();
  setup.cosineSim.corpusBuf.destroy();
  setup.cosineSim.queryBuf.destroy();
  setup.cosineSim.scalesBuf.destroy();
  setup.cosineSim.simsBuf.destroy();
  setup.cosineSim.readbackBuf.destroy();
}
